import { computed, inject, Injectable, Signal } from '@angular/core';
import { PermissionKey } from '../../constants/permissions';
import { FeatureSet } from '../../models/feature.model';
import { AuthService } from '../auth/auth.service';
import { UserDccsService } from '../user-dccs/user-dccs.service';
import { CasbinPermissionsService } from './casbin-permissions.service';
import { DccFeatureAccessService } from './dcc-feature-access.service';

/** A navigable feature gated by an optional Casbin permission and/or DCC feature flag. */
export interface FeatureAccessItem {
  /** Casbin permission in `resource:action` form, or `'admin'`. */
  permission?: string;
  /** DCC feature-flag name (per-DCC availability). */
  featureFlag?: string;
}

/**
 * Ordered list of DCC-scoped routes a user can land on after selecting a DCC.
 * Mirrors the DCC-scoped items in the sidebar (SidebarComponent.navGroups); keep
 * the two in sync. Order = landing priority for the dashboard "Continue" button.
 */
export const DCC_LANDING_ROUTES: (FeatureAccessItem & { route: string })[] = [
  { route: 'dock-receiving', permission: 'package:manage', featureFlag: 'Receive Packages' },
  { route: 'search-dock', permission: 'package:view', featureFlag: 'Dock Search' },
  { route: 'item-receiving', permission: 'photocapture:manage', featureFlag: 'Device Photo' },
  { route: 'secondary-clear-dashboard', permission: 'secondaryclearing:view', featureFlag: 'Secondary Clear Dashboard' },
  { route: 'secondary-clear-processing', permission: 'secondaryclearing:manage', featureFlag: 'Secondary Clear Processing' },
  { route: 'device-control-center', permission: 'devicecontrolcenter:manage', featureFlag: 'Device Control Center' },
];

/**
 * Single source of truth for "can the current user access this feature for this DCC".
 * Mirrors the route guards (dockPermissionGuard / adminGuard) so the navigation
 * (sidebar visibility, dashboard landing) stays consistent with what the guards allow.
 */
@Injectable({ providedIn: 'root' })
export class FeatureAccessService {
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(CasbinPermissionsService);
  private readonly dccFeatureAccess = inject(DccFeatureAccessService);
  private readonly userDccs = inject(UserDccsService);

  canAccess(item: FeatureAccessItem, dccCode?: string): boolean {
    // DCC-level feature availability applies to everyone, including admins
    // (mirrors dockPermissionGuard, where the feature-flag check precedes the
    // admin bypass). Only enforced once a DCC is known.
    if (
      item.featureFlag &&
      dccCode &&
      !this.dccFeatureAccess.hasAccess(item.featureFlag, dccCode)
    ) {
      return false;
    }

    if (!item.permission) return true;

    // Admins and full-access users can access everything else (mirrors adminGuard).
    if (this.auth.authProfile()?.isAdmin || this.permissions.hasFullAccess()) {
      return true;
    }

    // Admin-only items are inaccessible to everyone else.
    if (item.permission === 'admin') return false;

    const [resource, action] = item.permission.split(':') as [
      FeatureSet,
      string,
    ];

    // 'view' falls back to 'manage' — mirrors dockPermissionGuard.
    if (action === 'view') {
      return (
        this.permissions.hasPermission(resource, 'view', dccCode) ||
        this.permissions.hasPermission(resource, 'manage', dccCode)
      );
    }

    return this.permissions.hasPermission(resource, action, dccCode);
  }

  /**
   * Reactive form of {@link canAccess} for element-level UI gating. Returns a
   * memoised signal that re-evaluates when the permission set loads/reloads, when
   * the active DCC switches, and — for feature-flag gates — when the per-DCC flag
   * data loads. Reuses `canAccess` — it never re-implements the rule.
   *
   * Assign the returned signal to a field ONCE and read it in a template or
   * `computed`; do not call `canAccessSignal(key)()` inline in a template, which
   * would allocate a fresh signal on every change-detection tick.
   *
   * @param item A typed {@link PermissionKey} (e.g. `PERMISSIONS.vipManage`) or a
   *   full {@link FeatureAccessItem}. When the item carries a `featureFlag`, the
   *   per-DCC flag data is loaded automatically so the gate resolves correctly.
   * @param dccOverride Optional DCC code; defaults to the user's active DCC.
   */
  canAccessSignal(
    item: PermissionKey | FeatureAccessItem,
    dccOverride?: string,
  ): Signal<boolean> {
    const resolved: FeatureAccessItem =
      typeof item === 'string' ? { permission: item } : item;

    if (resolved.featureFlag) {
      void this.dccFeatureAccess.ensureLoaded();
    }

    return computed(() => {
      const dcc = dccOverride ?? this.userDccs.userAssignedDcc() ?? undefined;
      return this.canAccess(resolved, dcc);
    });
  }

  /**
   * The first DCC-scoped route the user can access for the given DCC, in
   * sidebar order. Ensures feature flags are loaded first. Returns null when
   * the user can access nothing for that DCC.
   */
  async firstAccessibleDccRoute(dccCode: string): Promise<string | null> {
    return this.resolveDccLandingRoute(dccCode);
  }

  /**
   * Resolves where to land for a DCC. Keeps `preferredRoute` (e.g. the page the
   * user is currently on) when that DCC can access it; otherwise falls back to
   * the first accessible route in sidebar order. Returns null when the user can
   * access nothing for that DCC.
   */
  async resolveDccLandingRoute(
    dccCode: string,
    preferredRoute?: string | null,
  ): Promise<string | null> {
    await this.dccFeatureAccess.ensureLoaded();

    if (preferredRoute) {
      const preferred = DCC_LANDING_ROUTES.find(
        (item) => item.route === preferredRoute,
      );
      if (preferred && this.canAccess(preferred, dccCode)) {
        return preferredRoute;
      }
    }

    return (
      DCC_LANDING_ROUTES.find((item) => this.canAccess(item, dccCode))?.route ??
      null
    );
  }
}
